import type { StepSummary } from '@sb/feathers-types';
import {
  MISSING_DEVICE_MARKER,
  type ActuateGripperArguments,
} from '@sb/integrations/implementations/gripper-general/steps/ActuateGripper/Arguments';
import type { WeldArguments } from '@sb/integrations/implementations/WeldMachine/shared/steps/Arguments';
import { DeviceKinds as WelderKinds } from '@sb/integrations/implementations/WeldMachine/shared/types/DeviceKinds';
import type { EquipmentItem } from '@sb/integrations/types';
import { optionBelongsToKind } from '@sb/integrations/utils';
import type { TCPOffsetOption } from '@sb/motion-planning';
import type { Configuration as StepConfiguration } from '@sb/remote-control/types/step/configuration';
import { listAllSteps } from '@sb/remote-control/util/listAllSteps';

export type EquipmentRefDiagnostic = {
  stepId: string;
  message: string;
  variant: 'alert' | 'warning';
};

const WELDER_KINDS = new Set<string>(WelderKinds);

function isWelder(item: EquipmentItem): boolean {
  return WELDER_KINDS.has(item.config.kind);
}

// Any CustomGripper qualifies, matching the Weld wizard's torch dropdown.
function isTorch(item: EquipmentItem): boolean {
  return item.config.kind === 'CustomGripper';
}

function lookupEquipmentById(
  equipment: EquipmentItem[],
  id: string | undefined,
): EquipmentItem | undefined {
  if (!id) return undefined;

  return equipment.find((eq) => eq.id === id);
}

type WeldRefLabels = {
  empty: string;
  missing: string;
  disabled: string;
};

function diagnoseWeldRef(
  currentId: string | undefined,
  equipment: EquipmentItem[],
  predicate: (item: EquipmentItem) => boolean,
  labels: WeldRefLabels,
): { message: string; variant: 'alert' | 'warning' } | undefined {
  if (!currentId) {
    return { message: labels.empty, variant: 'alert' };
  }

  const existing = lookupEquipmentById(equipment, currentId);

  if (existing && predicate(existing)) {
    if (!existing.isEnabled) {
      return { message: labels.disabled, variant: 'warning' };
    }

    return undefined;
  }

  return { message: labels.missing, variant: 'alert' };
}

function sanitizeWeldRef(
  currentId: string | undefined,
  equipment: EquipmentItem[],
  predicate: (item: EquipmentItem) => boolean,
): string | undefined {
  if (!currentId) return undefined;

  const existing = lookupEquipmentById(equipment, currentId);
  if (existing && predicate(existing)) return currentId;

  const enabledCandidate = equipment.find(
    (eq) => eq.isEnabled && predicate(eq),
  );

  if (enabledCandidate) return enabledCandidate.id;

  const disabledCandidate = equipment.find(predicate);

  return disabledCandidate?.id;
}

const WELD_MACHINE_LABELS: WeldRefLabels = {
  empty: 'No welder selected for this step.',
  missing: 'Configured welder is not present on this robot.',
  disabled:
    'Configured welder is disabled — enable it in the Equipment Manager before running.',
};

const WELD_TORCH_LABELS: WeldRefLabels = {
  empty: 'No torch selected for this step.',
  missing: 'Configured torch is not present on this robot.',
  disabled:
    'Configured torch is disabled — enable it in the Equipment Manager before running.',
};

const ACTUATE_GRIPPER_CLEARED_MESSAGE =
  'Cannot reference the original equipment in this step. Please open the step to update the gripper selection.';

const ACTUATE_GRIPPER_DELETED_MESSAGE =
  'Referenced gripper has been removed from this robot. Update or remove the gripper selection.';

const ZERO_FT_SENSOR_CLEARED_MESSAGE =
  'Cannot reference the original sensor in this step. Please open the step to update the force/torque sensor selection.';

const ZERO_FT_SENSOR_DELETED_MESSAGE =
  'Referenced force/torque sensor has been removed from this robot. Update or remove the sensor selection.';

const SET_ACTIVE_TOOLTIP_GRIPPER_DELETED_MESSAGE =
  'Referenced gripper has been removed from this robot. Update or remove the tooltip selection.';

const SET_ACTIVE_TOOLTIP_UNRESOLVABLE_MESSAGE =
  'No equipment on this robot offers the selected tooltip. Open the step and select a tooltip.';

export function sanitizeEquipmentReferences({
  steps,
  stepConfigurations,
  equipment,
}: {
  steps: StepSummary[];
  stepConfigurations: Record<string, StepConfiguration>;
  equipment: EquipmentItem[];
}): {
  sanitizedConfigurations: Record<string, StepConfiguration>;
} {
  const sanitizedConfigurations: Record<string, StepConfiguration> = {
    ...stepConfigurations,
  };

  for (const step of listAllSteps(steps)) {
    const config = stepConfigurations[step.id];
    if (!config) continue;

    const args = config.args as Record<string, unknown> | undefined;
    if (!args) continue;

    if (
      step.stepKind === 'ActuateGripper' ||
      step.stepKind === 'ZeroForceTorqueSensor'
    ) {
      // Both steps store { deviceID, deviceKind } and want the same cross-robot
      // remap: point a stale id at a same-kind device on the target robot.
      const { deviceID, deviceKind } = args as {
        deviceID?: string;
        deviceKind?: string;
      };

      if (deviceID && !lookupEquipmentById(equipment, deviceID)) {
        const sameKindCandidate = deviceKind
          ? equipment.find(
              (eq) => eq.isEnabled && eq.config.kind === deviceKind,
            ) ?? equipment.find((eq) => eq.config.kind === deviceKind)
          : undefined;

        sanitizedConfigurations[step.id] = {
          ...config,
          args: {
            ...args,
            deviceID: sameKindCandidate?.id ?? MISSING_DEVICE_MARKER,
          } as StepConfiguration['args'],
        };
      }
    } else if (step.stepKind === 'Weld') {
      const weldArgs = args as WeldArguments;

      const nextMachineID = sanitizeWeldRef(
        weldArgs.selectedMachineID,
        equipment,
        isWelder,
      );

      const nextTorchID = sanitizeWeldRef(
        weldArgs.selectedTorchID,
        equipment,
        isTorch,
      );

      if (
        nextMachineID !== weldArgs.selectedMachineID ||
        nextTorchID !== weldArgs.selectedTorchID
      ) {
        sanitizedConfigurations[step.id] = {
          ...config,
          args: {
            ...args,
            selectedMachineID: nextMachineID,
            selectedTorchID: nextTorchID,
          } as StepConfiguration['args'],
        };
      }
    }
  }

  return { sanitizedConfigurations };
}

export function findEquipmentRefDiagnostics({
  steps,
  stepConfigurations,
  equipment,
}: {
  steps: StepSummary[];
  stepConfigurations: Record<string, StepConfiguration>;
  equipment: EquipmentItem[];
}): EquipmentRefDiagnostic[] {
  const diagnostics: EquipmentRefDiagnostic[] = [];

  for (const step of listAllSteps(steps)) {
    const config = stepConfigurations[step.id];
    if (!config) continue;

    const args = config.args as Record<string, unknown> | undefined;
    if (!args) continue;

    if (step.stepKind === 'ActuateGripper') {
      const { deviceID } = args as ActuateGripperArguments;

      if (deviceID === undefined) continue;

      if (deviceID === MISSING_DEVICE_MARKER) {
        diagnostics.push({
          stepId: step.id,
          message: ACTUATE_GRIPPER_CLEARED_MESSAGE,
          variant: 'warning',
        });
      } else if (!lookupEquipmentById(equipment, deviceID)) {
        diagnostics.push({
          stepId: step.id,
          message: ACTUATE_GRIPPER_DELETED_MESSAGE,
          variant: 'alert',
        });
      }
    } else if (step.stepKind === 'ZeroForceTorqueSensor') {
      const { deviceID } = args as { deviceID?: string };

      if (deviceID === undefined) continue;

      if (deviceID === MISSING_DEVICE_MARKER) {
        diagnostics.push({
          stepId: step.id,
          message: ZERO_FT_SENSOR_CLEARED_MESSAGE,
          variant: 'warning',
        });
      } else if (!lookupEquipmentById(equipment, deviceID)) {
        diagnostics.push({
          stepId: step.id,
          message: ZERO_FT_SENSOR_DELETED_MESSAGE,
          variant: 'alert',
        });
      }
    } else if (step.stepKind === 'Weld') {
      const weldArgs = args as WeldArguments;

      const machineDiagnostic = diagnoseWeldRef(
        weldArgs.selectedMachineID,
        equipment,
        isWelder,
        WELD_MACHINE_LABELS,
      );

      const torchDiagnostic = diagnoseWeldRef(
        weldArgs.selectedTorchID,
        equipment,
        isTorch,
        WELD_TORCH_LABELS,
      );

      // Alert before warning so the first-wins merge in `useStepWarnings`
      // keeps the higher severity when both fields fire.
      const severityRank = (variant: 'alert' | 'warning') =>
        variant === 'alert' ? 0 : 1;

      const orderedDiagnostics = [machineDiagnostic, torchDiagnostic]
        .filter((d): d is NonNullable<typeof d> => d != null)
        .sort((a, b) => severityRank(a.variant) - severityRank(b.variant));

      for (const d of orderedDiagnostics) {
        diagnostics.push({ stepId: step.id, ...d });
      }
    } else if (step.stepKind === 'SetActiveTooltip') {
      const { tcpOption, gripperID } = args as {
        tcpOption?: TCPOffsetOption;
        gripperID?: string;
      };

      if (gripperID && !lookupEquipmentById(equipment, gripperID)) {
        diagnostics.push({
          stepId: step.id,
          message: SET_ACTIVE_TOOLTIP_GRIPPER_DELETED_MESSAGE,
          variant: 'alert',
        });
      } else if (
        tcpOption &&
        tcpOption !== 'wrist' &&
        tcpOption !== 'wristCamera' &&
        !equipment.some((item) =>
          optionBelongsToKind(tcpOption, item.config.kind),
        )
      ) {
        // An id-less step — a legacy numeric option, or one authored
        // before the selection pinned a device — whose option no equipment
        // kind on this robot offers. Name resolution can't succeed, so
        // playback will be blocked by validation. Surface it at import time
        // like the ActuateGripper missing-device path does.
        diagnostics.push({
          stepId: step.id,
          message: SET_ACTIVE_TOOLTIP_UNRESOLVABLE_MESSAGE,
          variant: 'alert',
        });
      }
    }
  }

  return diagnostics;
}
